import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { TranslateBlockDirective, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { StepPanel, StepPanels, Stepper } from 'primeng/stepper';
import { DeviceService } from '../../../../services/device.service';
import { PageLoaderService } from '../../../../services/page-loader.service';
import { ReportFlowService } from '../../../../services/report-flow.service';
import { ReportService } from '../../../../services/report.service';
import { ButtonIconComponent } from '../../../../shared/components/button-icon/button-icon.component';
import { StepperComponent } from '../../../../shared/components/stepper-component/stepper-component';
import {
  CreateReportDto,
  CreateReportResponseDto,
  ReportFilterDto,
  ReportMeasurementOptionsDto,
  ReportTime,
} from '../../../../types/dtos/reports.dto';
import { ReportExportFormat, ReportSteps, ReportType } from '../../../../types/enums/report.enum';
import { ReportCreatePreset } from '../../../../types/interfaces/report.interface';
import { StepperStepInterface } from '../../../../types/interfaces/stepper.interface';
import { ReportColumnStep } from './steps/column/column-step';
import { ColumnStepAlarm } from './steps/column/column-step-alarm';
import { ColumnStepDevice } from './steps/column/column-step-device';
import { ColumnStepLog } from './steps/column/column-step-log';
import { DateRange } from './steps/date-range/date-range';
import { ReportFilterAlarm } from './steps/filter/filter-alarm/filter-alarm';
import { FilterAlarmStep } from './steps/filter/filter-alarm/filter-alarm-step';
import { ReportFilterDevice } from './steps/filter/filter-device/filter-device';
import { FilterDeviceStep } from './steps/filter/filter-device/filter-device-step';
import { ReportFilterLog } from './steps/filter/filter-log/filter-log';
import { FilterLogStep } from './steps/filter/filter-log/filter-log-step';
import { ReportDownloadStep } from './steps/report-download/report-download';
import { ReportFormatStep } from './steps/report-format/report-format';
import { AppReportMeasurement } from './steps/report-measurement/app-report-measurement';
import { ReportTypeStep } from './steps/report-type/report-type';
import { ReportSelectDevice } from './steps/select/select-device/select-device';
import { SelectDeviceStep } from './steps/select/select-device/select-device-step';
import { SigiFiles } from './steps/sigi-files/sigi-files';
import {LogService} from '../../../../services/log.service';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../../types/enums/logging.enum';

@Component({
  selector: 'app-report-create',
  imports: [
    DateRange,
    FormsModule,
    ReportFormatStep,
    ReportTypeStep,
    StepPanel,
    StepPanels,
    Stepper,
    StepperComponent,
    ButtonIconComponent,
    AppReportMeasurement,
    SigiFiles,
    ReportFilterDevice,
    ReportColumnStep,
    ReportDownloadStep,
    ReportFilterAlarm,
    ReportFilterLog,
    ReportSelectDevice,
    TranslateBlockDirective,
  ],
  templateUrl: './report-create.html',
  styleUrl: './report-create.css',
})
export class ReportCreate implements OnInit, OnChanges {
  @Input()
  public preset: ReportCreatePreset | null = null;

  @Output()
  public close: EventEmitter<void> = new EventEmitter<void>();

  public loading: boolean = false;

  public reportForm: FormGroup = new FormGroup({
    reportType: new FormControl(),
    dateFrom: new FormControl(),
    dateTo: new FormControl(),
    format: new FormControl(ReportExportFormat.PDF),
  });
  public measurementReportOptions: ReportMeasurementOptionsDto = {
    rows: [],
    filter: {},
  };
  public columnStepAlarm: ColumnStepAlarm = new ColumnStepAlarm();
  public columnStepDevice: ColumnStepDevice = new ColumnStepDevice();
  public columnStepLog: ColumnStepLog = new ColumnStepLog();
  protected readonly ReportSteps = ReportSteps;
  protected readonly ReportType = ReportType;
  private selectDeviceStep: SelectDeviceStep = new SelectDeviceStep();
  private filterDeviceStep: FilterDeviceStep = new FilterDeviceStep();
  private filterAlarmStep: FilterAlarmStep = new FilterAlarmStep();
  private filterLogStep: FilterLogStep = new FilterLogStep();

  constructor(
    private readonly _reportService: ReportService,
    private readonly _messageService: MessageService,
    private readonly _reportFlowService: ReportFlowService,
    private readonly _translateService: TranslateService,
    private readonly _deviceService: DeviceService,
    private readonly _loggingService: LogService,
    private readonly _pageLoaderService: PageLoaderService,
  ) {
  }

  public get currentStep(): ReportSteps {
    return this._reportFlowService.getCurrentStep() || ReportSteps.SELECT_REPORT_TYPE;
  }

  public get deviceSelectForm(): FormGroup {
    return this.selectDeviceStep.getForm();
  }

  public get deviceFilterForm(): FormGroup {
    return this.filterDeviceStep.getForm();
  }

  public get alarmFilterForm(): FormGroup {
    return this.filterAlarmStep.getForm();
  }

  public get logFilterForm(): FormGroup {
    return this.filterLogStep.getForm();
  }

  public get createStepper(): StepperStepInterface[] {
    return this._reportFlowService.getAllSteps();
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['preset'] && this.preset) {
      await this._pageLoaderService.waitLoadingFinish();
      this._applyPreset(this.preset);
    }
  }

  public async ngOnInit(): Promise<void> {
    this._reportFlowService.start();

    await this._pageLoaderService.waitLoadingFinish();

    if (this.preset) {
      this._applyPreset(this.preset);
    }
  }

  public goBack(step: ReportSteps): void {
    this._reportFlowService.back();
  }

  public goToStep(step: ReportSteps): void {
    this._reportFlowService.goToStep(step);
  }

  public nextStep(step: ReportSteps): void {
    this._reportFlowService.setContext({
      reportType: this.reportType(),
      format: this.reportFormat(),
    });
    this._reportFlowService.next();
  }

  public canContinue(step: ReportSteps): boolean {
    return this._reportFlowService.canGoNext() ?? true;
  }

  public onMeasurementReportOptionsChange(options: ReportMeasurementOptionsDto): void {
    this.measurementReportOptions = options;
  }

  public doGenerate(): void {
    if (this.loading) return;
    let reportType: ReportType | undefined = this.reportType();
    let format: ReportExportFormat | undefined = this.reportFormat();

    if (!reportType) {
      this._messageService.add({
        severity: 'warn',
        summary: this._translateService.instant('common.messages.warning.missing-report-typ.summary'),
        detail: this._translateService.instant('common.messages.info.choose-report-type'),
        life: 3000,
      });
      return;
    }

    if (!format) {
      this._messageService.add({
        severity: 'warn',
        summary: this._translateService.instant('common.messages.summary.invalid-format'),
        detail: this._translateService.instant('common.messages.warning.invalid-format'),
        life: 3000,
      });
      return;
    }

    let generateReport: CreateReportDto = {
      type: reportType,
      rows: [],
    };

    /*
     * TIME SELECT
     */
    if (reportType != ReportType.DEVICE) {
      let selectedTime: ReportTime = this._selectedReportTime();

      generateReport.fromDate = selectedTime.fromDate;
      generateReport.toDate = selectedTime.toDate;
    }

    if (reportType === ReportType.DEVICE) {
      let deviceFilter: ReportFilterDto = this.filterDeviceStep.getStepFilter();
      generateReport.filter = deviceFilter.filter;

      if (format === ReportExportFormat.CSV) {
        generateReport.rows = this.columnStepDevice.getSelectedColumns();
      }
    }

    if (reportType === ReportType.DEVICE_DETAIL) {
      let deviceFilter: ReportFilterDto = this.selectDeviceStep.getStepFilter();
      generateReport.filter = deviceFilter.filter ?? undefined;
      generateReport.ids = deviceFilter.ids ?? undefined;
    }

    if (reportType === ReportType.ALARM) {
      let deviceFilter: ReportFilterDto = this.filterAlarmStep.getStepFilter();
      generateReport.filter = deviceFilter.filter;

      if (format === ReportExportFormat.CSV) {
        generateReport.rows = this.columnStepAlarm.getSelectedColumns();
      }
    }

    if (reportType === ReportType.LOG) {
      let deviceFilter: ReportFilterDto = this.filterLogStep.getStepFilter();
      generateReport.filter = deviceFilter.filter;

      if (format === ReportExportFormat.CSV) {
        generateReport.rows = this.columnStepLog.getSelectedColumns();
      }
    }

    if (reportType === ReportType.MEASUREMENT) {
      generateReport.rows = this.measurementReportOptions.rows;
      generateReport.filter = this.measurementReportOptions.filter;
    }

    this._generateReport(generateReport, format);
  }

  public reportType(): ReportType | undefined {
    return this.reportForm?.value?.['reportType'];
  }

  public reportFormat(): ReportExportFormat | undefined {
    return this.reportForm?.value?.['format'];
  }

  private _applyPreset(preset: ReportCreatePreset): void {
    this.reportForm.patchValue({
      reportType: preset.type,
      dateFrom: preset.from ? new Date(Number(preset.from) * 1000) : null,
      dateTo: preset.to ? new Date(Number(preset.to) * 1000) : null,
    }, {emitEvent: false});

    if (preset.type) {
      this.nextStep(ReportSteps.SELECT_REPORT_TYPE);
    }

    if (preset.from) {
      this.nextStep(ReportSteps.SELECT_TIME);
    }

    if (preset.device_eui) {
      let deviceId: number | undefined = this._deviceService.getDeviceIdByEui(preset.device_eui);
      if (deviceId) {
        this.deviceSelectForm.patchValue({
          selectType: 'select',
          ids: [deviceId],
        });
        this.nextStep(ReportSteps.SELECT_ELEMENTS);
      }
    }
  }

  private _selectedReportTime(): ReportTime {
    let from: string | undefined = this.reportForm?.value?.['dateFrom'];
    let to: string | undefined = this.reportForm?.value?.['dateTo'];

    let fromDateNumber: number | undefined = undefined;
    let toDateNumber: number | undefined = undefined;
    if (from) {
      let fromDate: Date = new Date(from);
      // Go to begin of date
      // fromDate.setHours(0, 0, 0, 0);
      fromDateNumber = fromDate.getTime();
    }
    if (to) {
      let toDate: Date = new Date(to);
      // Go to end of date
      // toDate.setHours(23, 59, 59, 999);
      toDateNumber = toDate.getTime();
    }

    return {fromDate: fromDateNumber, toDate: toDateNumber};
  }

  private _generateReport(
    report: CreateReportDto,
    format: ReportExportFormat,
  ) {
    this.loading = true;

    this._reportService.createReport(report, format)
      .subscribe({
        next: (response: CreateReportResponseDto) => {
          this._messageService.add({
            severity: 'info',
            summary: this._translateService.instant('common.title.generate-report'),
            detail: this._translateService.instant('common.messages.info.generating-report'),
          });

          this._loggingService.sendLog({
            target: LoggingTargetEnum.DEVICE,
            action: LoggingActionEnum.CREATE,
          });

          this.close.emit();
        },
        error: (error: unknown) => {
          this._messageService.add({
            severity: 'error',
            summary: this._translateService.instant('common.title.report-failed'),
            detail: this._translateService.instant('common.messages.error.during-creation'),
          });
        },
        complete: () => {
          this.loading = false;
        },
      });
  }
}
